import React, { useEffect, useState } from "react";
import { Box, Pagination } from "@mui/material";
import { useSearchParams } from "react-router-dom";

const CursorPagination = ({
  fetchFunction,
  basePayload = {},
  limit,
  dependency = [],
  onDataChange,
  responseMapper,
  onLoadingChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPage = Number(searchParams.get("page")) || 1;

  let urlCursorStack = [null];

  try {
    const savedCursors = searchParams.get("cursorStack");

    if (savedCursors) {
      const parsed = JSON.parse(savedCursors);

      if (Array.isArray(parsed) && parsed.length > 0) {
        urlCursorStack = parsed;
      }
    }
  } catch (error) {
    console.error("Invalid cursorStack in URL:", error);
  }

  const [currentPage, setCurrentPage] = useState(urlPage);
  const [cursorStack, setCursorStack] = useState(urlCursorStack);
  const [totalCount, setTotalCount] = useState(0);

  const updatePaginationUrl = (page, stack) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);

        if (page <= 1) {
          params.delete("page");
          params.delete("cursorStack");
        } else {
          params.set("page", String(page));

          params.set("cursorStack", JSON.stringify(stack));
        }

        return params;
      },
      {
        replace: true,
      },
    );
  };

  const fetchPage = async (cursor = null) => {
    try {
      onLoadingChange?.(true);

      const payload = {
        ...basePayload,
        limit,
      };

      if (cursor !== null && cursor !== undefined) {
        payload.after = cursor;
      }

      const response = await fetchFunction(payload);

      if (!response?.success && !response?.status) {
        return null;
      }

      const mapped = responseMapper(response);

      onDataChange?.(mapped.data);

      setTotalCount(mapped.totalCount || 0);

      return mapped;
    } catch (error) {
      console.error("Pagination fetch error:", error);

      return null;
    } finally {
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")) || 1;

    let stackFromUrl = [null];

    try {
      const savedCursors = searchParams.get("cursorStack");

      if (savedCursors) {
        const parsed = JSON.parse(savedCursors);

        if (Array.isArray(parsed) && parsed.length > 0) {
          stackFromUrl = parsed;
        }
      }
    } catch (error) {
      console.error("Invalid cursorStack:", error);
    }

    if (pageFromUrl > 1 && stackFromUrl.length >= pageFromUrl) {
      const cursor = stackFromUrl[pageFromUrl - 1];

      setCurrentPage(pageFromUrl);
      setCursorStack(stackFromUrl);

      fetchPage(cursor);

      return;
    }

    setCurrentPage(1);
    setCursorStack([null]);

    fetchPage(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependency);

  const handlePageChange = async (_, targetPage) => {
    if (targetPage === currentPage) {
      return;
    }

    if (targetPage <= cursorStack.length) {
      const cursor = targetPage === 1 ? null : cursorStack[targetPage - 1];

      setCurrentPage(targetPage);

      updatePaginationUrl(targetPage, cursorStack);

      await fetchPage(cursor);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    let tempCursorStack = [...cursorStack];

    let latestCursor = cursorStack[cursorStack.length - 1];

    let fetchedPage = cursorStack.length;

    while (fetchedPage < targetPage) {
      const payload = {
        ...basePayload,
        limit,
      };

      if (latestCursor !== null && latestCursor !== undefined) {
        payload.after = latestCursor;
      }

      const response = await fetchFunction(payload);

      if (!response?.success && !response?.status) {
        return;
      }

      const mapped = responseMapper(response);

      latestCursor = mapped.endCursor;

      tempCursorStack.push(latestCursor);

      fetchedPage++;
    }

    setCursorStack(tempCursorStack);

    const finalCursor =
      targetPage === 1 ? null : tempCursorStack[targetPage - 1];

    setCurrentPage(targetPage);

    updatePaginationUrl(targetPage, tempCursorStack);

    await fetchPage(finalCursor);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const totalPages = totalCount
    ? Math.max(1, Math.ceil(totalCount / limit))
    : currentPage;

  if (!fetchFunction || totalPages <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mt: 5,
        mb: 2,
      }}
    >
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handlePageChange}
        shape="rounded"
        siblingCount={1}
        boundaryCount={1}
        size="large"
        sx={{
          "& .MuiPagination-ul": {
            gap: 0,
          },

          "& .MuiPaginationItem-root": {
            fontWeight: 600,
            fontSize: "14px",
            minWidth: {
              xs: 30,
              sm: 38,
            },
            height: 38,
            borderRadius: "10px",
            transition: "all .2s ease",
          },

          "& .Mui-selected": {
            backgroundColor: "#002255 !important",
            color: "#fff",
          },

          "& .MuiPaginationItem-root:hover": {
            backgroundColor: "#f3f5f9",
          },
        }}
      />
    </Box>
  );
};

export default React.memo(CursorPagination);
